import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { StatusBadge } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import {
  CrmPanel, InboxPanel, ChatPanel, NotificationsPanel,
  DocumentsPanel, TransactionsPanel, FiscalPanel, NewsPanel,
} from "@/pages/admin/AdminPanels";
import { api, fmtErr, fmtUsd, fmtPrice, fmtDate } from "@/lib/api";
import {
  ShieldCheck, Users, ArrowLeftRight, Wallet, ScrollText, LogOut,
  Globe, Plus, Pencil, Trash2, CheckCircle2, XCircle, Ban, KeyRound,
} from "lucide-react";

const SYMBOLS = ["BTCUSDT","ETHUSDT","BNBUSDT","SOLUSDT","XRPUSDT","DOGEUSDT","ADAUSDT","TRXUSDT","LINKUSDT","LTCUSDT"];
const inputCls = "mt-1.5 bg-white/5 border-white/10 text-white";
const labelCls = "text-slate-300 text-xs";

function AdminLogin() {
  const { login } = useAuth();
  const { t } = useLang();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(identifier, password);
      if (user.role !== "admin") setError("Access denied — administrative credentials required.");
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f17] flex items-center justify-center px-5">
      <div className="glass rounded-2xl p-8 w-full max-w-sm" data-testid="admin-login-card">
        <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-5">
          <KeyRound size={20} className="text-red-400" />
        </div>
        <h1 className="font-display text-xl font-bold text-white">{t("admin.loginTitle")}</h1>
        <p className="text-xs text-slate-500 mt-1">{t("admin.loginSubtitle")}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className={labelCls}>{t("auth.identifier")}</Label>
            <Input data-testid="admin-identifier-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                   className={inputCls} autoComplete="username" required />
          </div>
          <div>
            <Label className={labelCls}>{t("auth.password")}</Label>
            <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   className={inputCls} autoComplete="current-password" required />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>
          )}
          <Button data-testid="admin-login-submit" type="submit" disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white h-11 font-semibold">
            {loading ? t("common.loading") : t("auth.loginBtn")}
          </Button>
        </form>
        <Link to="/" className="block text-center text-xs text-slate-500 hover:text-slate-300 mt-5">← UCP</Link>
      </div>
    </div>
  );
}

function Overview({ overview }) {
  const { t } = useLang();
  if (!overview) return null;
  const cards = [
    { icon: Users, label: t("admin.totalClients"), value: overview.clients_total },
    { icon: ShieldCheck, label: t("admin.pendingClients"), value: overview.clients_pending },
    { icon: CheckCircle2, label: t("admin.activeClients"), value: overview.clients_active },
    { icon: ArrowLeftRight, label: t("admin.pendingTrades"), value: overview.trades_pending },
    { icon: Wallet, label: t("admin.pendingFunding"), value: overview.funding_pending },
    { icon: ScrollText, label: t("admin.connections"), value: overview.connections_logged },
  ];
  return (
    <div data-testid="admin-overview">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500 uppercase tracking-wider">{c.label}</p>
              <c.icon size={16} className="text-emerald-400" />
            </div>
            <p className="font-ticker text-2xl font-semibold text-white mt-2">{c.value}</p>
          </div>
        ))}
      </div>
      <div className="glass rounded-2xl p-5 mt-4">
        <p className="text-xs text-slate-500 uppercase tracking-wider">{t("admin.totalUsd")}</p>
        <p className="font-ticker text-3xl font-semibold text-emerald-400 mt-2" data-testid="admin-total-usd">
          {fmtUsd(overview.total_client_usd)}
        </p>
      </div>
    </div>
  );
}

function Clients({ clients, reload }) {
  const { t } = useLang();
  const [balanceFor, setBalanceFor] = useState(null);
  const [adj, setAdj] = useState({ asset: "USD", amount: "", mode: "add" });
  const [addOpen, setAddOpen] = useState(false);
  const [newClient, setNewClient] = useState({ email: "", full_name: "", phone: "", password: "" });
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ email: "", full_name: "", phone: "", password: "" });

  const openEdit = (c) => {
    setEditTarget(c);
    setEditForm({ email: c.email, full_name: c.full_name, phone: c.phone || "", password: "" });
  };

  const submitEditClient = async (e) => {
    e.preventDefault();
    try {
      const payload = { email: editForm.email, full_name: editForm.full_name, phone: editForm.phone };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/admin/clients/${editTarget.id}`, payload);
      toast.success(t("admin.clientUpdated"));
      setEditTarget(null);
      reload();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/clients/${id}/status`, { status });
      toast.success(t("admin.statusUpdated"));
      reload();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/clients/${balanceFor.id}/balance`, {
        asset: adj.asset, amount: parseFloat(adj.amount), mode: adj.mode,
      });
      toast.success(t("admin.balanceUpdated"));
      setBalanceFor(null);
      setAdj({ asset: "USD", amount: "", mode: "add" });
      reload();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const submitNewClient = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/clients", newClient);
      toast.success(t("admin.clientCreated"));
      setAddOpen(false);
      setNewClient({ email: "", full_name: "", phone: "", password: "" });
      reload();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const clientStatusBadge = (s) => {
    const map = {
      active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
      pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
      rejected: "bg-red-500/10 text-red-400 border-red-500/30",
      suspended: "bg-slate-500/10 text-slate-400 border-slate-500/30",
    };
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-medium ${map[s] || map.pending}`}
            data-testid={`client-status-${s}`}>
        {s}
      </span>
    );
  };

  return (
    <div data-testid="admin-clients">
      <div className="flex justify-end mb-4">
        <Button data-testid="add-client-btn" size="sm" onClick={() => setAddOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-500 text-white">
          <Plus size={14} className="mr-1.5" /> {t("admin.addClient")}
        </Button>
      </div>
      <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="clients-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="text-left px-4 py-3">{t("admin.client")}</th>
              <th className="text-left px-4 py-3">{t("admin.registeredAt")}</th>
              <th className="text-left px-4 py-3">{t("admin.ip")}</th>
              <th className="text-left px-4 py-3">{t("dash.status")}</th>
              <th className="text-right px-4 py-3">USD</th>
              <th className="text-right px-4 py-3">{t("admin.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`client-row-${c.email}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-white text-sm">{c.full_name}</p>
                  <p className="text-xs text-slate-500">{c.email} · {c.phone || "—"}</p>
                </td>
                <td className="px-4 py-3 text-xs font-ticker text-slate-400">{fmtDate(c.created_at)}</td>
                <td className="px-4 py-3 text-xs font-ticker text-slate-400">{c.registration_ip || "—"}</td>
                <td className="px-4 py-3">{clientStatusBadge(c.status)}</td>
                <td className="px-4 py-3 text-right font-ticker text-white">
                  {fmtUsd(c.balances?.USD ?? 0)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5 flex-wrap">
                    {c.status !== "active" && (
                      <Button size="sm" variant="ghost" data-testid={`approve-${c.id}`}
                              className="h-8 px-2 text-emerald-400 hover:bg-emerald-500/10"
                              title={t("admin.approve")} onClick={() => setStatus(c.id, "active")}>
                        <CheckCircle2 size={15} />
                      </Button>
                    )}
                    {c.status === "active" && (
                      <Button size="sm" variant="ghost" data-testid={`suspend-${c.id}`}
                              className="h-8 px-2 text-slate-400 hover:bg-white/5"
                              title={t("admin.suspend")} onClick={() => setStatus(c.id, "suspended")}>
                        <Ban size={15} />
                      </Button>
                    )}
                    {c.status === "pending" && (
                      <Button size="sm" variant="ghost" data-testid={`reject-${c.id}`}
                              className="h-8 px-2 text-red-400 hover:bg-red-500/10"
                              title={t("admin.reject")} onClick={() => setStatus(c.id, "rejected")}>
                        <XCircle size={15} />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`edit-client-${c.id}`}
                            className="h-8 px-2 text-amber-400 hover:bg-amber-500/10"
                            title={t("admin.editClient")} onClick={() => openEdit(c)}>
                      <Pencil size={15} />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`balance-${c.id}`}
                            className="h-8 px-2 text-cyan-400 hover:bg-cyan-500/10"
                            title={t("admin.adjustBalance")}
                            onClick={() => setBalanceFor(c)}>
                      <Wallet size={15} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!balanceFor} onOpenChange={() => setBalanceFor(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{t("admin.adjustBalance")} — {balanceFor?.full_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAdjust} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}>{t("admin.asset")}</Label>
                <Select value={adj.asset} onValueChange={(v) => setAdj({ ...adj, asset: v })}>
                  <SelectTrigger data-testid="adjust-asset-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["USD", ...SYMBOLS.map((s) => s.replace("USDT", ""))].map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>{t("admin.mode")}</Label>
                <Select value={adj.mode} onValueChange={(v) => setAdj({ ...adj, mode: v })}>
                  <SelectTrigger data-testid="adjust-mode-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">{t("admin.add")}</SelectItem>
                    <SelectItem value="set">{t("admin.set")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className={labelCls}>{t("admin.amount")}</Label>
              <Input data-testid="adjust-amount-input" type="number" step="any" required
                     value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })}
                     className={`${inputCls} font-ticker`} />
            </div>
            <Button data-testid="adjust-submit-btn" type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
              {t("common.save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#111827] border-white/10 text-white">
          <DialogHeader><DialogTitle>{t("admin.addClient")}</DialogTitle></DialogHeader>
          <form onSubmit={submitNewClient} className="space-y-4 mt-2">
            <div>
              <Label className={labelCls}>{t("auth.fullName")}</Label>
              <Input data-testid="newclient-name-input" value={newClient.full_name} required
                     onChange={(e) => setNewClient({ ...newClient, full_name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <Label className={labelCls}>{t("auth.email")}</Label>
              <Input data-testid="newclient-email-input" type="email" value={newClient.email} required
                     onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}>{t("auth.phone")}</Label>
                <Input data-testid="newclient-phone-input" value={newClient.phone}
                       onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} className={inputCls} />
              </div>
              <div>
                <Label className={labelCls}>{t("auth.password")}</Label>
                <Input data-testid="newclient-password-input" value={newClient.password} required minLength={8}
                       onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} className={inputCls} />
              </div>
            </div>
            <Button data-testid="newclient-submit-btn" type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
              {t("admin.addClient")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{t("admin.editClient")} — {editTarget?.full_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEditClient} className="space-y-4 mt-2">
            <div>
              <Label className={labelCls}>{t("auth.email")}</Label>
              <Input data-testid="editclient-email-input" type="email" value={editForm.email} required
                     onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}>{t("auth.fullName")}</Label>
                <Input data-testid="editclient-name-input" value={editForm.full_name} required
                       onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <Label className={labelCls}>{t("auth.phone")}</Label>
                <Input data-testid="editclient-phone-input" value={editForm.phone}
                       onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <Label className={labelCls}>{t("admin.newPassword")}</Label>
              <Input data-testid="editclient-password-input" type="text" value={editForm.password} minLength={8}
                     placeholder="••••••••"
                     onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} className={`${inputCls} font-ticker`} />
            </div>
            <Button data-testid="editclient-submit-btn" type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
              {t("common.save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Trades({ trades, clients, reload }) {
  const { t } = useLang();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTrade, setEditTrade] = useState(null);
  const [form, setForm] = useState({ user_id: "", symbol: "BTCUSDT", side: "buy", amount_usd: "", price: "", status: "executed", note: "" });

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/trades", {
        ...form, amount_usd: parseFloat(form.amount_usd), price: parseFloat(form.price),
      });
      toast.success(t("admin.tradeSaved"));
      setCreateOpen(false);
      setForm({ user_id: "", symbol: "BTCUSDT", side: "buy", amount_usd: "", price: "", status: "executed", note: "" });
      reload();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/trades/${editTrade.id}`, {
        amount_usd: parseFloat(editTrade.amount_usd),
        price: parseFloat(editTrade.price),
        status: editTrade.status,
        note: editTrade.note || "",
      });
      toast.success(t("admin.tradeSaved"));
      setEditTrade(null);
      reload();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const quickStatus = async (id, status) => {
    try {
      await api.patch(`/admin/trades/${id}`, { status });
      toast.success(t("admin.statusUpdated"));
      reload();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/trades/${id}`);
      toast.success(t("admin.tradeDeleted"));
      reload();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="admin-trades">
      <div className="flex justify-end mb-4">
        <Button data-testid="simulate-trade-btn" size="sm" onClick={() => setCreateOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-500 text-white">
          <Plus size={14} className="mr-1.5" /> {t("admin.newTrade")}
        </Button>
      </div>
      <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]" data-testid="admin-trades-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="text-left px-4 py-3">{t("dash.time")}</th>
              <th className="text-left px-4 py-3">{t("admin.client")}</th>
              <th className="text-left px-4 py-3">{t("dash.symbol")}</th>
              <th className="text-left px-4 py-3">{t("dash.side")}</th>
              <th className="text-right px-4 py-3">{t("dash.amount")}</th>
              <th className="text-right px-4 py-3">{t("dash.price")}</th>
              <th className="text-left px-4 py-3">{t("dash.status")}</th>
              <th className="text-right px-4 py-3">{t("admin.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((tr) => (
              <tr key={tr.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-trade-row-${tr.id}`}>
                <td className="px-4 py-3 text-xs font-ticker text-slate-400">{fmtDate(tr.created_at)}</td>
                <td className="px-4 py-3 text-xs text-slate-300">{tr.user_email}</td>
                <td className="px-4 py-3 font-semibold text-white">{tr.symbol.replace("USDT", "")}/USDT</td>
                <td className={`px-4 py-3 text-xs font-semibold ${tr.side === "buy" ? "text-emerald-400" : "text-red-400"}`}>
                  {tr.side.toUpperCase()}
                </td>
                <td className="px-4 py-3 text-right font-ticker text-white">{fmtUsd(tr.amount_usd)}</td>
                <td className="px-4 py-3 text-right font-ticker text-slate-300">${fmtPrice(tr.price)}</td>
                <td className="px-4 py-3"><StatusBadge status={tr.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    {tr.status === "pending" && (
                      <>
                        <Button size="sm" variant="ghost" data-testid={`validate-trade-${tr.id}`}
                                className="h-8 px-2 text-emerald-400 hover:bg-emerald-500/10" title={t("admin.validate")}
                                onClick={() => quickStatus(tr.id, "executed")}>
                          <CheckCircle2 size={15} />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`reject-trade-${tr.id}`}
                                className="h-8 px-2 text-red-400 hover:bg-red-500/10" title={t("admin.reject")}
                                onClick={() => quickStatus(tr.id, "rejected")}>
                          <XCircle size={15} />
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`edit-trade-${tr.id}`}
                            className="h-8 px-2 text-cyan-400 hover:bg-cyan-500/10" title={t("admin.edit")}
                            onClick={() => setEditTrade({ ...tr })}>
                      <Pencil size={15} />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`delete-trade-${tr.id}`}
                            className="h-8 px-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10" title={t("admin.delete")}
                            onClick={() => remove(tr.id)}>
                      <Trash2 size={15} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#111827] border-white/10 text-white">
          <DialogHeader><DialogTitle>{t("admin.newTrade")}</DialogTitle></DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4 mt-2">
            <div>
              <Label className={labelCls}>{t("admin.client")}</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })} required>
                <SelectTrigger data-testid="trade-client-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name} — {c.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}>{t("dash.symbol")}</Label>
                <Select value={form.symbol} onValueChange={(v) => setForm({ ...form, symbol: v })}>
                  <SelectTrigger data-testid="trade-symbol-select-admin" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SYMBOLS.map((s) => <SelectItem key={s} value={s}>{s.replace("USDT", "")}/USDT</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>{t("dash.side")}</Label>
                <Select value={form.side} onValueChange={(v) => setForm({ ...form, side: v })}>
                  <SelectTrigger data-testid="trade-side-select-admin" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buy">BUY</SelectItem>
                    <SelectItem value="sell">SELL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}>{t("dash.amount")}</Label>
                <Input data-testid="trade-amount-admin" type="number" step="any" required
                       value={form.amount_usd} onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
                       className={`${inputCls} font-ticker`} />
              </div>
              <div>
                <Label className={labelCls}>{t("dash.price")}</Label>
                <Input data-testid="trade-price-admin" type="number" step="any" required
                       value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                       className={`${inputCls} font-ticker`} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}>{t("dash.status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="trade-status-select-admin" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="executed">{t("dash.executedBadge")}</SelectItem>
                    <SelectItem value="pending">{t("dash.pendingBadge")}</SelectItem>
                    <SelectItem value="rejected">{t("dash.rejectedBadge")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>{t("dash.note")}</Label>
                <Input data-testid="trade-note-admin" value={form.note}
                       onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} />
              </div>
            </div>
            <Button data-testid="trade-submit-admin" type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
              {t("common.save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTrade} onOpenChange={() => setEditTrade(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white">
          <DialogHeader><DialogTitle>{t("admin.edit")} — {editTrade?.symbol}</DialogTitle></DialogHeader>
          {editTrade && (
            <form onSubmit={submitEdit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={labelCls}>{t("dash.amount")}</Label>
                  <Input data-testid="edit-trade-amount" type="number" step="any" required value={editTrade.amount_usd}
                         onChange={(e) => setEditTrade({ ...editTrade, amount_usd: e.target.value })}
                         className={`${inputCls} font-ticker`} />
                </div>
                <div>
                  <Label className={labelCls}>{t("dash.price")}</Label>
                  <Input data-testid="edit-trade-price" type="number" step="any" required value={editTrade.price}
                         onChange={(e) => setEditTrade({ ...editTrade, price: e.target.value })}
                         className={`${inputCls} font-ticker`} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={labelCls}>{t("dash.status")}</Label>
                  <Select value={editTrade.status} onValueChange={(v) => setEditTrade({ ...editTrade, status: v })}>
                    <SelectTrigger data-testid="edit-trade-status" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="executed">{t("dash.executedBadge")}</SelectItem>
                      <SelectItem value="pending">{t("dash.pendingBadge")}</SelectItem>
                      <SelectItem value="rejected">{t("dash.rejectedBadge")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={labelCls}>{t("dash.note")}</Label>
                  <Input data-testid="edit-trade-note" value={editTrade.note || ""}
                         onChange={(e) => setEditTrade({ ...editTrade, note: e.target.value })} className={inputCls} />
                </div>
              </div>
              <Button data-testid="edit-trade-submit" type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                {t("common.save")}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FundingAdmin({ funding, reload }) {
  const { t } = useLang();
  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/funding/${id}/status`, { status });
      toast.success(t("admin.statusUpdated"));
      reload();
    } catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <div className="glass rounded-2xl overflow-hidden overflow-x-auto" data-testid="admin-funding">
      <table className="w-full text-sm min-w-[860px]" data-testid="admin-funding-table">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
            <th className="text-left px-4 py-3">{t("dash.time")}</th>
            <th className="text-left px-4 py-3">{t("admin.client")}</th>
            <th className="text-left px-4 py-3">{t("admin.type")}</th>
            <th className="text-left px-4 py-3">{t("admin.details")}</th>
            <th className="text-left px-4 py-3">{t("dash.status")}</th>
            <th className="text-right px-4 py-3">{t("admin.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {funding.map((f) => (
            <tr key={f.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`funding-row-${f.id}`}>
              <td className="px-4 py-3 text-xs font-ticker text-slate-400">{fmtDate(f.created_at)}</td>
              <td className="px-4 py-3 text-xs text-slate-300">{f.user_email}</td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                  {f.type === "bank" ? "SEPA / IBAN" : `${f.asset} — ${f.network}`}
                </span>
              </td>
              <td className="px-4 py-3 text-xs font-ticker text-slate-400 max-w-[280px] truncate">
                {f.type === "bank" ? `${f.iban} · ${f.bank_name} · ${f.bic}` : f.address}
              </td>
              <td className="px-4 py-3"><StatusBadge status={f.status} /></td>
              <td className="px-4 py-3">
                {f.status === "pending" && (
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="ghost" data-testid={`validate-funding-${f.id}`}
                            className="h-8 px-2 text-emerald-400 hover:bg-emerald-500/10" title={t("admin.validate")}
                            onClick={() => setStatus(f.id, "validated")}>
                      <CheckCircle2 size={15} />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`reject-funding-${f.id}`}
                            className="h-8 px-2 text-red-400 hover:bg-red-500/10" title={t("admin.reject")}
                            onClick={() => setStatus(f.id, "rejected")}>
                      <XCircle size={15} />
                    </Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Logs({ logs }) {
  const { t } = useLang();
  return (
    <div className="glass rounded-2xl overflow-hidden overflow-x-auto" data-testid="admin-logs">
      <table className="w-full text-sm min-w-[860px]" data-testid="admin-logs-table">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
            <th className="text-left px-4 py-3">{t("admin.when")}</th>
            <th className="text-left px-4 py-3">{t("auth.email")}</th>
            <th className="text-left px-4 py-3">{t("admin.ip")}</th>
            <th className="text-left px-4 py-3">{t("dash.status")}</th>
            <th className="text-left px-4 py-3">{t("admin.userAgent")}</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`log-row-${l.id}`}>
              <td className="px-4 py-3 text-xs font-ticker text-slate-400">{fmtDate(l.timestamp)}</td>
              <td className="px-4 py-3 text-xs text-slate-300">{l.email}</td>
              <td className="px-4 py-3 text-xs font-ticker text-cyan-400">{l.ip}</td>
              <td className="px-4 py-3">
                <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                  l.status === "success" || l.status === "register"
                    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                    : "bg-red-500/10 text-red-400 border-red-500/30"
                }`}>
                  {l.status}
                </span>
              </td>
              <td className="px-4 py-3 text-[11px] text-slate-500 max-w-[260px] truncate">{l.user_agent}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPortal() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState([]);
  const [trades, setTrades] = useState([]);
  const [funding, setFunding] = useState([]);
  const [logs, setLogs] = useState([]);

  const isAdmin = user && user.role === "admin";

  const reload = () => {
    if (!isAdmin) return;
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/admin/trades").then((r) => setTrades(r.data)).catch(() => {});
    api.get("/admin/funding").then((r) => setFunding(r.data)).catch(() => {});
    api.get("/admin/logs").then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(reload, [isAdmin]);

  if (user === null)
    return <div className="min-h-screen bg-[#0b0f17] flex items-center justify-center text-slate-400 text-sm">{t("common.loading")}</div>;
  if (!isAdmin) return <AdminLogin />;

  return (
    <div className="min-h-screen bg-[#0b0f17]" data-testid="admin-portal">
      <header className="glass-strong sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo withWordmark={false} size={36} />
            <div>
              <p className="font-display font-bold text-white text-sm">{t("admin.title")}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-red-400/80">Restricted</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setLang(lang === "en" ? "fr" : "en")} data-testid="admin-lang-toggle"
                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-300 border border-white/10 rounded-full px-3 py-1.5 hover:text-white">
              <Globe size={12} /> {lang === "en" ? "FR" : "EN"}
            </button>
            <span className="text-xs text-slate-400 hidden sm:block">{user.email}</span>
            <Button variant="outline" size="sm" data-testid="admin-logout-btn"
                    className="border-white/15 text-slate-300 hover:bg-white/5"
                    onClick={async () => { await logout(); navigate("/"); }}>
              <LogOut size={14} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 py-8">
        <Tabs defaultValue="overview">
          <TabsList className="glass bg-transparent p-1 flex-wrap h-auto" data-testid="admin-tabs">
            <TabsTrigger value="overview" data-testid="admin-tab-overview" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.overview")}</TabsTrigger>
            <TabsTrigger value="clients" data-testid="admin-tab-clients" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.clients")}</TabsTrigger>
            <TabsTrigger value="trades" data-testid="admin-tab-trades" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.trades")}</TabsTrigger>
            <TabsTrigger value="funding" data-testid="admin-tab-funding" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.funding")}</TabsTrigger>
            <TabsTrigger value="logs" data-testid="admin-tab-logs" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.logs")}</TabsTrigger>
            <TabsTrigger value="crm" data-testid="admin-tab-crm" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.crm")}</TabsTrigger>
            <TabsTrigger value="inbox" data-testid="admin-tab-inbox" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.inbox")}</TabsTrigger>
            <TabsTrigger value="chat" data-testid="admin-tab-chat" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.chat")}</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="admin-tab-notifications" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.notificationsTab")}</TabsTrigger>
            <TabsTrigger value="documents" data-testid="admin-tab-documents" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.documents")}</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="admin-tab-transactions" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.transactions")}</TabsTrigger>
            <TabsTrigger value="fiscal" data-testid="admin-tab-fiscal" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.fiscalTab")}</TabsTrigger>
            <TabsTrigger value="news" data-testid="admin-tab-news" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">{t("admin.news")}</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-6"><Overview overview={overview} /></TabsContent>
          <TabsContent value="clients" className="mt-6"><Clients clients={clients} reload={reload} /></TabsContent>
          <TabsContent value="trades" className="mt-6"><Trades trades={trades} clients={clients} reload={reload} /></TabsContent>
          <TabsContent value="funding" className="mt-6"><FundingAdmin funding={funding} reload={reload} /></TabsContent>
          <TabsContent value="logs" className="mt-6"><Logs logs={logs} /></TabsContent>
          <TabsContent value="crm" className="mt-6"><CrmPanel /></TabsContent>
          <TabsContent value="inbox" className="mt-6"><InboxPanel /></TabsContent>
          <TabsContent value="chat" className="mt-6"><ChatPanel /></TabsContent>
          <TabsContent value="notifications" className="mt-6"><NotificationsPanel clients={clients} /></TabsContent>
          <TabsContent value="documents" className="mt-6"><DocumentsPanel /></TabsContent>
          <TabsContent value="transactions" className="mt-6"><TransactionsPanel /></TabsContent>
          <TabsContent value="fiscal" className="mt-6"><FiscalPanel /></TabsContent>
          <TabsContent value="news" className="mt-6"><NewsPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
